import type {ApiWork} from '@/types/api';
import type {CatalogResponse} from '@/lib/api/public';

/**
 * Шаблонные фото двух демо-студий лежат в public/template (чёрно-серые, красятся в акцент студии) и подставляются поверх данных из базы:
 * загрузка в хранилище Supabase требует прав из миграции 0010 (см. SETUP.md).
 * Настоящие студии этого не касается: у них фото приходят из базы как обычно.
 */
const DEMO_MEDIA: Record<string, {hero: string; works: {url: string; caption: string}[]}> = {
  'graphite-detailing': {
    hero: '/template/hero.jpg',
    works: [
      {url: '/template/work-1.jpg', caption: 'Задняя часть'},
      {url: '/template/work-2.jpg', caption: 'Капот и оптика'},
      {url: '/template/work-3.jpg', caption: 'Общий вид'},
    ],
  },
  'lumen-detailing': {
    hero: '/template/hero.jpg',
    works: [
      {url: '/template/work-1.jpg', caption: 'Задняя часть'},
      {url: '/template/work-2.jpg', caption: 'Капот и оптика'},
    ],
  },
};

/** Демо под студии из базы рассылки: те же шаблонные фото, пока у студии нет своих. */
export const OUTREACH_DEMOS = [
  'goodzone-samara', 'prosvet-kazan', 'avtohype-ekb', 'pdr54-novosibirsk', 'mgstyle-omsk',
  'elitegarage-perm', 'toplook-krasnoyarsk', 'karma-ufa', 'globus-krasnodar', 'garage19-voronezh',
  'rds-chelyabinsk', 'a3-novosibirsk', 'smart-detailing-kazan', 'blesk-ekb', 'primelab-krasnoyarsk',
  'vosk-omsk', 'artroyal-ufa', 'rosa-perm', 'ardetailing-voronezh', 'detailing-sam-samara',
  'chizh-avto-krasnodar', 'legen-auto-rostov', 'polimer-spb', 'crystal-city-moscow', 'pdr-service-voronezh', 'bezpokraski-v8-kazan', 'autolab-rcc-ekb', 'sman-lab-novosibirsk', 'black-duck-chelyabinsk', 'avtoestetika102-ufa',
  'street-light-chelyabinsk', 'ghetto-krasnoyarsk', 'btc-ekb', 'perfectionist-omsk', 'polysher-novosibirsk', 'bitbox-ufa', 'vap-service-krasnodar', 'levsha-samara', 'omega-detail-novosibirsk', 'depo-detailing-chelyabinsk',
];
for (const slug of OUTREACH_DEMOS) DEMO_MEDIA[slug] = DEMO_MEDIA['graphite-detailing']!;

export function withDemoMedia(slug: string, data: CatalogResponse): CatalogResponse {
  const media = DEMO_MEDIA[slug];
  if (!media) return data;
  const works: ApiWork[] = data.works.length
    ? data.works.map((work, index) => {
        const replacement = media.works[index];
        return replacement ? {...work, imageUrl: replacement.url, caption: replacement.caption} : work;
      })
    : media.works.map((work, index) => ({id: `demo-${index}`, imageUrl: work.url, caption: work.caption, sort: index + 1}));
  return {...data, tenant: {...data.tenant, heroImageUrl: media.hero}, works};
}

/** Шаблонные серые фото лежат в /template/: только им нужна подкраска бликов под акцент. */
export function isTemplateImage(url: string | null | undefined): url is string {
  return Boolean(url && url.startsWith('/template/'));
}

/** Маска ярких мест для шаблонного фото: hero.jpg -> hero-hl.png. */
export function highlightUrl(url: string): string {
  return url.replace(/\.jpg$/, '-hl.png');
}
